import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ContactoPersonaRepository } from 'src/app/domain/service/persona/contacto-persona-repository';
import { ContactoPersona, ContactoPersonaRequest } from 'src/app/domain/model/persona/contacto-persona';
import { CursorPageResponse } from 'src/app/domain/model/cumplimiento/cumplimiento';
import { getDniAutenticado } from 'src/app/common/auth-utils';
import { validarSize } from 'src/app/common/validation-utils';
import { DireccionCursor } from 'src/app/domain/model/common/page-response.dto';

const HEADER_USER_DNI = 'X-User-DNI';
const QUERY_PARAM_ACTIVO = 'activo';
const ACTIVO_SI = 'S';
const PATH_CENTRO_TRABAJO = 'centro-trabajo';
const QUERY_PARAM_CURSOR = 'cursor';
const CDPERSONA_PLACEHOLDER = 0;

/**
 * Adaptador HTTP: HttpContactoPersonaRepository
 *
 * Implementa el puerto ContactoPersonaRepository para comunicación HTTP con backend.
 * Realiza llamadas REST al backend Spring Boot (ContactoPersonaController).
 *
 * NOTA: Solo aplica para personas jurídicas (NIT)
 *
 * Endpoints:
 * - GET /api/personas/{cdpersona}/contactos
 * - GET /api/personas/{cdpersona}/contactos/{id}
 * - POST /api/personas/{cdpersona}/contactos
 * - PUT /api/personas/{cdpersona}/contactos/{id}
 * - DELETE /api/personas/{cdpersona}/contactos/{id}
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Injectable({ providedIn: 'root' })
export class HttpContactoPersonaRepository implements ContactoPersonaRepository {

  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/personas`;

  /**
   * Construye la URL para contactos de una persona
   */
  private contactosUrl(cdpersona: number): string {
    return `${this.baseUrl}/${cdpersona}/contactos`;
  }

  /**
   * Obtiene el DNI del usuario en sesión.
   */
  private getDni(): string {
    return getDniAutenticado();
  }

  /**
   * Construye los headers HTTP con el DNI del usuario.
   */
  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      [HEADER_USER_DNI]: this.getDni()
    });
  }

  /**
   * Obtiene UNA página de contactos de una persona (keyset/cursor en servidor).
   * Sin cursor y direction=NEXT → primera página; direction=LAST sin cursor → última.
   */
  async getByPersonaPaged(
    cdpersona: number,
    soloActivos: boolean,
    cursor: string | null,
    size: number,
    direction: DireccionCursor = 'NEXT'
  ): Promise<CursorPageResponse<ContactoPersona>> {
    validarSize(size);
    const paramsObj: Record<string, string> = { size: String(size), direction };
    if (soloActivos) paramsObj[QUERY_PARAM_ACTIVO] = ACTIVO_SI;
    if (cursor) paramsObj[QUERY_PARAM_CURSOR] = cursor;
    const resp: any = await firstValueFrom(
      this.http.get<any>(this.contactosUrl(cdpersona), {
        params: new HttpParams({ fromObject: paramsObj })
      })
    );
    return this.normalizarPagina(resp, size);
  }

  /** Normaliza la respuesta del backend (PageResponse) a CursorPageResponse. */
  private normalizarPagina(resp: any, size: number): CursorPageResponse<ContactoPersona> {
    return {
      content: Array.isArray(resp?.content) ? resp.content : [],
      size: resp?.size || size,
      nextCursor: resp?.nextCursor ?? null,
      previousCursor: resp?.previousCursor ?? null,
      hasNext: !!resp?.hasNext,
      hasPrevious: !!resp?.hasPrevious,
      totalElements: resp?.totalElements ?? undefined
    };
  }

  /**
   * Crea un nuevo contacto
   */
  create(cdpersona: number, contacto: ContactoPersonaRequest): Promise<ContactoPersona> {
    return firstValueFrom(
      this.http.post<ContactoPersona>(this.contactosUrl(cdpersona), contacto, { headers: this.getHeaders() })
    );
  }

  /**
   * Actualiza un contacto existente
   */
  update(cdpersona: number, nmconsecutivo: number, contacto: ContactoPersonaRequest): Promise<ContactoPersona> {
    return firstValueFrom(
      this.http.put<ContactoPersona>(`${this.contactosUrl(cdpersona)}/${nmconsecutivo}`, contacto, { headers: this.getHeaders() })
    );
  }

  /**
   * Inactiva un contacto (soft delete).
   * limpiarRolCargo=true desde el popup de la orden (legacy ListaContactosViewImpl anula CDROL y CDCARGO).
   */
  delete(cdpersona: number, nmconsecutivo: number, limpiarRolCargo = false): Promise<void> {
    const params = new HttpParams({ fromObject: { limpiarRolCargo: String(limpiarRolCargo) } });
    return firstValueFrom(
      this.http.delete<void>(`${this.contactosUrl(cdpersona)}/${nmconsecutivo}`, { headers: this.getHeaders(), params })
    );
  }

  /** Obtiene UNA página de contactos de un centro de trabajo (keyset/cursor en servidor). */
  async getByCentroTrabajoPaged(
    nmconsecCt: number,
    cursor: string | null,
    size: number,
    direction: DireccionCursor = 'NEXT'
  ): Promise<CursorPageResponse<ContactoPersona>> {
    validarSize(size);
    const paramsObj: Record<string, string> = { size: String(size), direction };
    if (cursor) paramsObj[QUERY_PARAM_CURSOR] = cursor;
    const url = `${this.contactosUrl(CDPERSONA_PLACEHOLDER)}/${PATH_CENTRO_TRABAJO}/${nmconsecCt}`;
    const resp: any = await firstValueFrom(
      this.http.get<any>(url, { params: new HttpParams({ fromObject: paramsObj }) })
    );
    return this.normalizarPagina(resp, size);
  }

  /**
   * Inactiva un contacto desde el popup de la orden con centro de trabajo.
   * Legacy ListaContactosViewImpl: SNACTIVO='N', FEBAJA y CDROL/CDCARGO en null en
   * TPSC_PERSONAS_CONTACTOS; el vínculo en TPSC_CONTAC_CENTRO_TRAB no se modifica.
   *
   * DELETE /api/servicios/personas/{cdpersona}/contactos/{nmconsecutivo}/centro-trabajo/{nmconsecCt}
   */
  deleteWithCentroTrabajo(
    cdpersona: number,
    nmconsecutivo: number,
    nmconsecCentroTrabajo: number
  ): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(
        `${this.contactosUrl(cdpersona)}/${nmconsecutivo}/centro-trabajo/${nmconsecCentroTrabajo}`,
        { headers: this.getHeaders() }
      )
    );
  }

  /**
   * Crea un contacto en base a una orden de servicio.
   * POST /api/servicios/personas/ordenes/{nmconsecOrden}/contactos
   */
  /** Obtiene UNA página de contactos de una orden (centro de trabajo o, si no tiene, beneficiario). */
  async getByOrdenPaged(
    nmconsecOrden: number,
    cursor: string | null,
    size: number,
    direction: DireccionCursor = 'NEXT'
  ): Promise<CursorPageResponse<ContactoPersona>> {
    validarSize(size);
    const paramsObj: Record<string, string> = { size: String(size), direction };
    if (cursor) paramsObj[QUERY_PARAM_CURSOR] = cursor;
    const resp: any = await firstValueFrom(
      this.http.get<any>(`${this.baseUrl}/ordenes/${nmconsecOrden}/contactos`, {
        params: new HttpParams({ fromObject: paramsObj })
      })
    );
    return this.normalizarPagina(resp, size);
  }

  /** Devuelve el contacto de la orden con ese correo o lo crea (legacy crearNuevoContacto). */
  resolverDesdeOrden(nmconsecOrden: number, contacto: ContactoPersonaRequest): Promise<ContactoPersona> {
    return firstValueFrom(
      this.http.post<ContactoPersona>(
        `${this.baseUrl}/ordenes/${nmconsecOrden}/contactos/resolver`,
        contacto,
        { headers: this.getHeaders() }
      )
    );
  }

  /** Devuelve el contacto del centro de trabajo con ese correo o lo crea (legacy crearNuevoContacto sin OC). */
  resolverDesdeCentroTrabajo(
    cdpersona: number,
    nmconsecCentroTrabajo: number,
    contacto: ContactoPersonaRequest
  ): Promise<ContactoPersona> {
    return firstValueFrom(
      this.http.post<ContactoPersona>(
        `${this.contactosUrl(cdpersona)}/${PATH_CENTRO_TRABAJO}/${nmconsecCentroTrabajo}/resolver`,
        contacto,
        { headers: this.getHeaders() }
      )
    );
  }

  createFromOrden(nmconsecOrden: number, contacto: ContactoPersonaRequest): Promise<ContactoPersona> {
    return firstValueFrom(
      this.http.post<ContactoPersona>(
        `${this.baseUrl}/ordenes/${nmconsecOrden}/contactos`,
        contacto,
        { headers: this.getHeaders() }
      )
    );
  }
}
